import { useState } from "react";
import type { Inquiry } from "../types";
import { decideProposal } from "../eventsApi";

// ReservationSystem #484: the guest said no to a pending Group Event's Proposal
// (Proposal Sent), or it is reopened. The Reservation System holds the status;
// HQ shows a declined inquiry as lost, in the Declined column.
const REASONS = { price: "Price", date_unavailable: "Date unavailable", booked_elsewhere: "Booked elsewhere", no_response: "No response", other: "Other" };
type Result = Awaited<ReturnType<typeof decideProposal>>;

export function ProposalDecision({ inquiry, onDone }: { inquiry: Inquiry; onDone: (result: Result) => void }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reopen = inquiry.status === "lost";
  // Only an unpaid Proposal can be declined; a paid event is cancelled in the Reservation System.
  const pending = (inquiry.reservation_status as { reservationStatus?: string } | null)?.reservationStatus === "pending";
  if (!inquiry.reservation_id || !(reopen || (inquiry.status === "quoted" && pending))) return null;

  const submit = async () => {
    if (!reopen && !reason) return setError("Choose why the guest declined.");
    if (!reopen && reason === "other" && !note.trim()) return setError("Describe the reason when choosing Other.");
    if (reopen && !note.trim()) return setError("Record why the Proposal is being reopened.");
    setBusy(true); setError(null);
    try {
      onDone(await decideProposal(inquiry.id, reopen ? { op: "reopen", reason: note.trim() } : { op: "decline", reason, ...(note.trim() ? { note: note.trim() } : {}) }));
      setOpen(false); setReason(""); setNote("");
    } catch (err) { setError((err as Error).message || "The Reservation System did not answer. Try again."); }
    finally { setBusy(false); }
  };

  const label = reopen ? "Reopen Proposal" : "Proposal Declined";
  if (!open) return (
    <button type="button" onClick={() => setOpen(true)}
      className="w-full rounded-md border border-tribal-border px-3 py-2 text-xs font-medium text-white hover:bg-tribal-border transition-colors">
      {label}
    </button>
  );
  return (
    <div className="space-y-2 rounded-md border border-tribal-border p-2">
      {!reopen && (
        <label className="block text-xs text-tribal-muted">Why the guest declined
          <select value={reason} onChange={e => setReason(e.target.value)} className="mt-1 w-full rounded bg-tribal-bg border border-tribal-border px-2 py-1 text-xs text-white">
            <option value="">Choose a reason</option>
            {Object.entries(REASONS).map(([value, text]) => <option key={value} value={value}>{text}</option>)}
          </select>
        </label>
      )}
      <label className="block text-xs text-tribal-muted">{reopen ? "Why reopen it" : "Note (required for Other)"}
        <input value={note} maxLength={1000} onChange={e => setNote(e.target.value)} className="mt-1 w-full rounded bg-tribal-bg border border-tribal-border px-2 py-1 text-xs text-white" />
      </label>
      {!reopen && <p className="text-xs text-tribal-muted">The deposit link stops working. No email goes to the guest.</p>}
      {reopen && <p className="text-xs text-tribal-muted">It goes back to Proposal Sent. Edit Quote in the new system makes a new version with a new deposit link.</p>}
      {error && <p className="text-xs text-red-400">{error}</p>}
      <div className="flex gap-2">
        <button type="button" disabled={busy} onClick={submit} className="flex-1 rounded-md bg-tribal-red px-3 py-1.5 text-xs font-medium text-white hover:bg-tribal-red-dark disabled:opacity-50">
          {busy ? "Saving…" : label}
        </button>
        <button type="button" disabled={busy} onClick={() => { setOpen(false); setError(null); }} className="rounded-md px-3 py-1.5 text-xs text-tribal-muted hover:text-white">Cancel</button>
      </div>
    </div>
  );
}
